// The Delivered by day FOLLOW-UP: one short note, on the same thread, with a 72-hour deadline.
//
// Greg, 2026-10-27: "a final chance to accept their offer... give each group 72 hours and just
// change the emails day by day as they go." The date is the day it is written plus three
// (lib/delivered-followup.js deadlineFor), and comes in on the queue row. No default: an email
// with the wrong deadline is worse than no email.
//
// ⚠️ SAME OFFER, TO THE PENNY. The price is the FIRST send's own offer_price, read from its row on
// the server. The free ones stay free. Nothing is recomputed from the order.
//
// ⚠️ SHORT ON PURPOSE, AND THE "NO THANKS" LINE IS NOT A COURTESY. Same reasons as
// winbackFollowupEmail.js: the first email is quoted underneath, and a one-to-one email has no
// unsubscribe link, so "no thanks" is the alternative to the spam button. A reply that is plainly
// "no thanks" takes them off the VIP offers (applyNoThanks, lib/vip-winback-followup.js).
//
// ⚠️ NO RENEWAL DATE. The first email named one worked out from the day it was sent; a rep sets
// the membership up when they answer. Repeating a weeks-old date here would be wrong for some.
//
// Brand rules apply (customer-facing): no em or en dashes, no AI tells.

const money = (n) => '$' + Number(n).toFixed(2);

const esc = (s) => String(s == null ? '' : s)
  .replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

const firstNameOf = (full) => {
  const n = String(full || '').trim();
  if (!n || n.toLowerCase().startsWith('(no name')) return 'there';
  return n.split(/\s+/)[0];
};

// "2026-09-22" -> "September 22".
const longDay = (iso) => {
  if (!iso) return '';
  const d = new Date(String(iso).slice(0, 10) + 'T12:00:00');
  return Number.isNaN(+d) ? '' : d.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
};

export function buildDeliveredFollowupEmail(customer, rep) {
  if (!customer.claimByLabel) throw new Error('No reply-by date for ' + (customer.name || 'this customer') + '.');
  const first = esc(firstNameOf(customer.name));
  const when = longDay(customer.firstSent);
  const free = !(Number(customer.offerPrice) > 0);
  const orderNo = customer.orderNumber ? ' #' + esc(customer.orderNumber) : '';
  const subject = `${firstNameOf(customer.name)}, following up on your Crab Place VIP Club offer`;

  // A moved customer never heard from this rep, and this is a new email with nothing quoted under
  // it, so it says who wrote first. lib/delivered-followup.js has the rule.
  const opening = customer.movedFrom
    ? `my colleague ${esc(customer.movedFrom)} wrote to you${when ? ' on ' + when : ' a little while ago'} about
  joining our VIP Club and I wanted to make sure it reached you.`
    : `I wrote to you${when ? ' on ' + when : ' a little while ago'} about
  joining our VIP Club and wanted to make sure it reached you.`;

  const offerLine = free
    ? `Your free year of VIP Club membership is still waiting for you, and I am holding it until ${customer.claimByLabel}.`
    : `Your VIP Club membership for ${money(customer.offerPrice)} is still waiting for you, and I am holding that price until ${customer.claimByLabel}.`;
  const why = free
    ? `That is a full year at no charge instead of the usual $79.99, because of what you would have saved on your order${orderNo}.`
    : `That is instead of the usual $79.99, because of what you would have saved on your order${orderNo}.`;

  const html = `
<div style="font-family:Calibri,Arial,sans-serif;font-size:15px;line-height:1.5;color:#1a1a1a;max-width:600px">
  <p style="margin:0 0 14px">Hi ${first}, ${opening}</p>

  <p style="background:#fff3ed;border-left:3px solid #DC582A;padding:14px 16px;margin:18px 0">
    <b style="color:#00395C;font-size:16px">${offerLine}</b><br>
    ${why} After ${customer.claimByLabel} the offer expires.
  </p>

  <p style="margin:0 0 14px">Members get 8% off seafood, discounted shipping and 3% cash back on every order.</p>

  <p style="margin:0 0 14px"><b>Just reply "I'm in" and I will set it up for you.</b> There is nothing
  for you to fill in.</p>

  <p style="margin:0 0 22px;color:#59707E">If you would rather pass, reply "no thanks" and I will not ask again.</p>

  <p style="margin:0 0 6px">Thanks ${first},</p>

  <p style="margin:0">
  <b style="color:#00395C">${esc(rep.name || 'The Crab Place')}</b><br>
  <span style="color:#59707E;font-size:14px">The Crab Place${rep.email ? ' &middot; ' + esc(rep.email) : ''}</span></p>
</div>`.trim();

  return { subject, html, to: customer.email || '' };
}
